import clsx from "clsx";
import get from "lodash/get";
import React, { useRef } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";

import Download from "~/components/Icons/default/Download";
import ButtonResolver from "~/components/Table/TableRow/DefaultTableRow/Addition/ButtonResolver";
import Button from "~/shared/ui/Fields/Button";
import Tooltip from "~/shared/ui/Fields/Tooltip";
import {
  RowHandlerByOptionNameFirstLayer,
  RowHandlerByOptionNameSecondLayer,
} from "~/components/Table/TableRow/DefaultTableRow/Addition/RowHandler";
import {
  isAnnouncementSignedBy_DefaultTableRow,
  isAnnouncementTitle_DefaultTableRow,
  isCoursesTableCell_DefaultTableRow,
  isImportanceBoth_DefaultTableRow,
  isSortActive_DefaultTableRow,
  isTdStatus_DefaultTableRow,
  isTrTitle_DefaultTableRow,
  isUsersRemoved_DefaultTableRow,
} from "~/components/Table/TableRow/DefaultTableRow/utils";
import { useGetLocationData } from "~/hooks/locationData";
import { useDownloadPeriodInfoLazyQuery } from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";

import { ROW_CHECK_OPTIONS } from "./options";

import s from "./TableRow.less";

interface IProps {
  options: string[];
  nameChildren?: string[];
  type: string;
  btnDownload?: boolean;
  btnCustom?: (props: any) => JSX.Element;
  btnEdit?: boolean;
  btnView?: boolean;
  btnVersion?: boolean;
  btnDel?: boolean;
  btnEditStatus?: boolean;
  item;
  rowCanBeClicked?: ((item: any) => boolean) | boolean;
  rowClickHandler?: (item: any) => void;
  rowValuesDecorator?: (
    value: any,
    optionName: string,
    dataObject?: any
  ) => any;
}

const DefaultTableRow: React.FC<IProps> = ({
  options = [],
  nameChildren = [],
  item = {},
  btnDownload = false,
  btnCustom,
  btnEdit = false,
  btnView = false,
  btnDel = false,
  btnVersion = false,
  btnEditStatus = false,
  type,
  rowCanBeClicked,
  rowClickHandler,
  rowValuesDecorator,
}) => {
  const history = useHistory();
  const { sortField } = useGetLocationData();
  const lang = localStorage.getItem("language");
  const withBtns =
    btnDownload ||
    btnEdit ||
    btnDel ||
    btnView ||
    btnVersion ||
    btnCustom ||
    btnEditStatus;

  const textAreaRef = useRef(null);

  function copyToClipboard(e) {
    textAreaRef.current.select();
    document.execCommand("copy");
    // This is just personal preference.
    // I prefer to not show the whole text area selected.
    e.target.focus();
  }

  const { notifyReport } = useNotify();
  const [DownloadPeriodInfo] = useDownloadPeriodInfoLazyQuery({
    onCompleted(data) {
      const hash = get(
        data,
        `DealerRegistryGetter.downloadPeriodInfo.fileHash`
      );
      hash && notifyReport(hash);
    },
    fetchPolicy: "no-cache",
  });

  const { path } = useRouteMatch();
  const isRowWillClickable = (() => {
    if (typeof rowCanBeClicked === "function") {
      return rowClickHandler && rowCanBeClicked(item);
    } else {
      return rowClickHandler && rowCanBeClicked;
    }
  })();

  const goToId = (e) => {
    if (isRowWillClickable) {
      rowClickHandler(item);
    }

    if (path !== "/dealers/inheritance/") {
      (e.target.dataset.option_name === ROW_CHECK_OPTIONS.DealerName ||
        (type === "dealerPeriod" &&
          e.target.dataset.option_name === ROW_CHECK_OPTIONS.id)) &&
        history.push(`${item.id}/`);
    } else {
      e.target.dataset.option_name === ROW_CHECK_OPTIONS.id &&
        item.ArchivePeriodId &&
        history.push(`/dealers/period/${item.ArchivePeriodId}/${item.id}`);
    }
  };

  const uploadArchive = () => {
    DownloadPeriodInfo({
      variables: {
        periodId: item.id,
      },
    });
  };

  const commonName = lang === "en" ? nameChildren[1] : nameChildren[0];

  return (
    <tr className={clsx(s.row_tr, { [s.hoverRow]: isRowWillClickable })}>
      {options?.map((optionName, index) => {
        return (
          <td
            style={{
              maxWidth:
                optionName === "status" ||
                optionName === "appealStatusFrontend" ||
                optionName === "resolutionStatusFrontend"
                  ? 150
                  : 100,
              overflow: optionName === "status" ? "visible" : "hidden",
              fontWeight:
                (optionName === "positions" &&
                  item.positionNumber === 1 &&
                  "bold") ||
                "normal",
            }}
            className={clsx({
              [s.sort_active]: isSortActive_DefaultTableRow(
                optionName,
                sortField,
                type,
                commonName,
                lang
              ),
              [s.remove]: isUsersRemoved_DefaultTableRow(item, type),
              [s.trTitle]: isTrTitle_DefaultTableRow(optionName, type),
              [s.tdStatus]: isTdStatus_DefaultTableRow(optionName, type),
              [s.cell]: isCoursesTableCell_DefaultTableRow(optionName, type),
            })}
            key={index}
            data-option_name={optionName}
            onClick={goToId}
          >
            <div>
              <RowHandlerByOptionNameFirstLayer
                type={type}
                item={item}
                optionName={optionName}
              />
              {optionName === "status" && item?.message ? (
                <Tooltip text={item.message}>
                  <span
                    className={clsx(s.cont, {
                      [s.hoverEffectCont]: isAnnouncementTitle_DefaultTableRow(
                        optionName,
                        type
                      ),
                      [s.elementEffect]: isAnnouncementSignedBy_DefaultTableRow(
                        optionName,
                        type
                      ),
                      [s.effectContCenter]: isImportanceBoth_DefaultTableRow(
                        optionName,
                        type
                      ),
                      [s.freeWidth]: isAnnouncementTitle_DefaultTableRow(
                        optionName,
                        type
                      ),
                    })}
                  >
                    <RowHandlerByOptionNameSecondLayer
                      type={type}
                      item={item}
                      optionName={optionName}
                      copyToClipboard={copyToClipboard}
                      textAreaRef={textAreaRef}
                      rowValuesDecorator={rowValuesDecorator}
                    />
                  </span>
                </Tooltip>
              ) : (
                <span
                  className={clsx(s.cont, {
                    [s.hoverEffectCont]: isAnnouncementTitle_DefaultTableRow(
                      optionName,
                      type
                    ),
                    [s.elementEffect]: isAnnouncementSignedBy_DefaultTableRow(
                      optionName,
                      type
                    ),
                    [s.effectContCenter]: isImportanceBoth_DefaultTableRow(
                      optionName,
                      type
                    ),
                    [s.freeWidth]: isAnnouncementTitle_DefaultTableRow(
                      optionName,
                      type
                    ),
                  })}
                >
                  <RowHandlerByOptionNameSecondLayer
                    type={type}
                    item={item}
                    optionName={optionName}
                    copyToClipboard={copyToClipboard}
                    textAreaRef={textAreaRef}
                    rowValuesDecorator={rowValuesDecorator}
                  />
                </span>
              )}
            </div>
          </td>
        );
      })}

      {withBtns && (
        <ButtonResolver
          type={type}
          item={item}
          btnEdit={btnEdit}
          btnView={btnView}
          btnEditStatus={btnEditStatus}
          btnDel={btnDel}
          btnDownload={btnDownload}
          btnCustom={btnCustom}
        />
      )}

      {type === "dealerPeriod" && (
        <td>
          <div className={s.cont}>
            <Button small txtBtn={"Скачать архив"} onClick={uploadArchive}>
              <Download />
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
};
export default DefaultTableRow;
